/**
 * Sky bake + sky dome shader sources. Owner: lighting.
 *
 * The bake material is rendered by a CubeCamera into a HDR cube render target
 * (atmosphere + clouds, no sun disc — the disc is the analytic directional
 * light so putting it in the IBL would double count).
 *
 * The dome material samples that cube at mip 0 and adds the sun disc with
 * wavelength-dependent limb darkening, so the visible sun stays crisp.
 */

import { NOISE_GLSL } from './noise.glsl.js';
import { ATMOSPHERE_GLSL } from './atmosphere.glsl.js';
import { CLOUDS_GLSL } from './clouds.glsl.js';

export const SKY_VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
}
`;

export const SKY_BAKE_FRAG = /* glsl */ `
precision highp float;
${NOISE_GLSL}
${ATMOSPHERE_GLSL}
${CLOUDS_GLSL}

uniform float uAltitude;
uniform vec3  uSkyAmbient;         // rough hemispheric sky radiance, CPU side
uniform vec3  uCloudSunRadiance;   // solar illuminance at cloud deck, CPU side
uniform float uExposureGuard;      // clamps values that would overflow fp16
uniform float uUnitScale;          // nits -> render units (see Sky.js)

varying vec3 vDir;

void main() {
  vec3 dir = normalize( vDir );

  vec3 tView;
  vec3 sky = aaaSkyRadiance( dir, uSunDirection, uAltitude, tView );

  vec3 cloudRad;
  float cloudT = aaaCloudMarch( dir, uSunDirection, uCloudSunRadiance, uSkyAmbient, uAltitude, cloudRad );

  /**
   * Aerial perspective on the cloud deck, from the atmosphere rather than from a
   * curve.
   *
   * It used to be mix( cloudRad, sky, (1 - smoothstep(0.02, 0.42, dir.y)) * 0.8 ),
   * i.e. an elevation ramp that reached full strength at the horizon and did not
   * release until 25 deg. A 45.7 deg vertical FOV at eye level puts *all* of the
   * visible sky under 23 deg, so every cloud in every hero frame was being mixed
   * 30-60% toward flat sky colour — measured against the real extinction below,
   * two to four times too much at 8-15 deg. That, together with the
   * un-normalised scattering in the march, is why the deck read as grey card
   * instead of as cloud (FINDINGS F13).
   *
   * The physical version: density falls off exponentially with altitude, so the
   * mean density along a slant path between two altitudes is just its vertical
   * average, and the optical depth is that average times the slant distance.
   * Ozone is centred at 25 km and contributes nothing down here.
   *
   * ### The pedestal, and why the deck read flat in gameplay frames
   *
   * The extinction above is correct; what was wrong was what replaced it.
   * sky is aaaSkyRadiance, the in-scatter integrated along the WHOLE path
   * to space, and the previous line substituted all of it — sky * (1 - tAerial)
   * — for a path that only reaches the cloud base at 1500 m. Only the part of
   * the column that lies *below* the deck can scatter into this segment, and
   * that share is 1 - exp( -h / H ) per species: 0.17 for Rayleigh (H = 8 km)
   * but 0.71 for Mie (H = 1.2 km). So the blue half of the pedestal was being
   * over-added by about 6x.
   *
   * It matters exactly where the gameplay frames look. light_sky is pitched
   * up at 45-70 deg, where the slant distance to the deck is ~1.8 km and the
   * pedestal is a couple of percent either way — which is why that shot showed
   * a lit rim, a self-shadowed underside and billow detail. level_square and
   * level_rooftop see the same deck at 15-30 deg, where the slant distance is
   * 3-5 km: the old term put 25-35% of a flat, smooth sky value on top of every
   * cloud pixel, and a constant added to a patch is precisely how you delete
   * its contrast. Measured on the review's own box, level_square (600-920,
   * 80-260): sRGB sd 0.072 against 0.124 in the iteration-1 build.
   */
  float hDeck = max( uCloudBase - uAltitude, 1.0 );
  float rhoR = ( AAA_Hr / hDeck ) * ( exp( -uAltitude / AAA_Hr ) - exp( -uCloudBase / AAA_Hr ) );
  float rhoM = ( AAA_Hm / hDeck ) * ( exp( -uAltitude / AAA_Hm ) - exp( -uCloudBase / AAA_Hm ) );
  float dDeck = max( aaaShellDistance( uAltitude, dir.y, uCloudBase ), 0.0 );

  vec3 odR = AAA_betaR * rhoR * dDeck;
  float odM = AAA_betaMe * uTurbidity * rhoM * dDeck;
  vec3 tAerial = exp( -( odR + vec3( odM ) ) );

  // Share of each species' vertical column that sits below the deck — i.e. the
  // fraction of the sky's own in-scatter that this segment can actually
  // generate.
  float shareR = 1.0 - exp( -hDeck / AAA_Hr );
  float shareM = 1.0 - exp( -hDeck / AAA_Hm );
  vec3 inFrac = clamp(
      ( 1.0 - exp( -odR ) ) * shareR + ( 1.0 - exp( -vec3( odM ) ) ) * shareM,
      0.0, 1.0 );

  // Where there is no cloud, sky already carries the whole path's in-scatter,
  // so the haze term has to be weighted by the cloud's own opacity or a clear
  // sky gets washed twice.
  float opacity = 1.0 - cloudT;
  vec3 col = sky * cloudT + cloudRad * tAerial + sky * opacity * inFrac;

  col = min( col, vec3( uExposureGuard ) ) * uUnitScale;
  gl_FragColor = vec4( col, 1.0 );
}
`;

export const SKY_DOME_FRAG = /* glsl */ `
precision highp float;
${NOISE_GLSL}

uniform samplerCube uSkyCube;
uniform vec3  uSunDirection;
uniform vec3  uSunDiscRadiance;   // nits, already clamped for fp16
uniform float uSunAngularRadius;
uniform float uSkyIntensity;
uniform float uTexelAngle;   // angular size of one cube texel, radians

varying vec3 vDir;

/**
 * Five-tap tent across one cube texel.
 *
 * The cube is 1024/face = 0.088 deg per texel; a 62 deg vertical FOV over 1080
 * lines is 0.057 deg per pixel, so ONE cube texel is 1.5 screen pixels. A cloud
 * silhouette in this march goes from transmittance 1 to 0 inside a single
 * sample — sigma is 0.05 /m at unit density and a step is tens of metres — so
 * bilinear magnification turns that into a hard 1-2 px edge with visible
 * staircase, which is exactly what the review found at (600,340)->(760,430).
 *
 * Filtering here rather than at the bake is deliberate: it costs four extra
 * cube taps on one full-screen box and it widens *every* high-frequency
 * artefact in the cube at once — the cutout edge and the ray-march quadrature
 * ripple in the cloud interiors — without touching the shape, which is a
 * ~100 m feature and 20+ texels across.
 */
vec3 aaaSkySoft( vec3 dir ) {
  vec3 up = abs( dir.y ) < 0.95 ? vec3( 0.0, 1.0, 0.0 ) : vec3( 1.0, 0.0, 0.0 );
  vec3 t = normalize( cross( up, dir ) );
  vec3 b = cross( dir, t );
  float e = uTexelAngle;
  vec3 c = textureLod( uSkyCube, dir, 0.0 ).rgb * 0.36;
  c += textureLod( uSkyCube, normalize( dir + t * e ), 0.0 ).rgb * 0.16;
  c += textureLod( uSkyCube, normalize( dir - t * e ), 0.0 ).rgb * 0.16;
  c += textureLod( uSkyCube, normalize( dir + b * e ), 0.0 ).rgb * 0.16;
  c += textureLod( uSkyCube, normalize( dir - b * e ), 0.0 ).rgb * 0.16;
  return c;
}

void main() {
  vec3 dir = normalize( vDir );
  vec3 col = aaaSkySoft( dir ) * uSkyIntensity;

  // Analytic sun disc with wavelength dependent limb darkening.
  float cosT = clamp( dot( dir, uSunDirection ), -1.0, 1.0 );
  float ang = acos( cosT );
  float edge = uSunAngularRadius * 0.06;
  float inDisc = 1.0 - smoothstep( uSunAngularRadius - edge, uSunAngularRadius + edge, ang );
  if ( inDisc > 0.0 ) {
    float r = clamp( ang / uSunAngularRadius, 0.0, 1.0 );
    float mu = sqrt( max( 0.0, 1.0 - r * r ) );
    vec3 limb = pow( vec3( max( mu, 1e-3 ) ), vec3( 0.397, 0.503, 0.652 ) );
    col += uSunDiscRadiance * limb * inDisc;
  }

  // Dither: 12 bit of gradient in a huge dynamic range bands badly otherwise.
  float d = ( aaaIGN( gl_FragCoord.xy ) - 0.5 );
  col *= 1.0 + d * 0.004;

  gl_FragColor = vec4( col, 1.0 );
}
`;
